import axios from 'axios';
import { TutorResponse, ImageSolveResponse } from '../types';
import { authService } from './auth';
import { getTenantFromHostname } from '../utils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const PUBLIC_KEY = process.env.REACT_APP_PUBLIC_KEY;

const defaultHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
};

if (PUBLIC_KEY) {
  defaultHeaders['x-app-key'] = PUBLIC_KEY;
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: defaultHeaders,
});

apiClient.interceptors.request.use(async (config) => {
  const token = await authService.getIdToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }

  const tenant = getTenantFromHostname();
  config.headers = config.headers ?? {};
  config.headers['x-tenant-id'] = tenant || 'default';
  return config;
});

export const tutorApi = {
  // Fetch solution for a question
  getSolution: async (question: string): Promise<TutorResponse> => {
    const response = await apiClient.post('/solve-structured', { question });
    return response.data;
  },

  // Fetch solution from image (OCR + solve)
  solveFromImage: async (file: File): Promise<ImageSolveResponse> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await apiClient.post('/solve-from-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Health check
  healthCheck: async (): Promise<boolean> => {
    try {
      await apiClient.get('/health');
      return true;
    } catch {
      return false;
    }
  },
};

export interface RegisterUserPayload {
  firebaseUid: string;
  email: string | null;
  displayName: string | null;
  provider: 'google' | 'email';
  tenant: string;
  token: string;
}

export const authApi = {
  // Register user with backend
  register: async (payload: RegisterUserPayload): Promise<void> => {
    const { token, ...body } = payload;
    await apiClient.post('/auth/register', body, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },
};

export default apiClient;
